import { forwardRef, useImperativeHandle, useRef, useState, type ReactElement } from 'react';
import { debug } from 'zf-web-infra';
import * as Base from '@base';
import { Box, useTranslation } from '@common';
import styles from '../../style.module.less';
import { CellEmpty, CellEnable, CellOptional, CellType } from '../grid-designer/types';
import { service } from './service';
import modalStyles from './style.module.less';
import { ModalFooter } from './footer';
import type { CellPos } from '../grid-designer';
import type { CustomInputModalForm, CustomInputModalProps, CustomInputModalRef, InputCellSetting, FilledCellSetting, Condition } from './types';

function CustomInputModalComponent(props: CustomInputModalProps, ref: CustomInputModalRef): ReactElement {
  const confirm = useRef<(value: InputCellSetting) => void>(() => null);
  const [t] = useTranslation();
  const [show, setShow] = useState<boolean>(false);
  const [initValue, setInitValue] = useState<InputCellSetting>();
  const [condition, setCondition] = useState<FilledCellSetting['condition']>([]);

  // 表单实例
  const [form] = Base.Form.useForm<CustomInputModalForm>();
  const empty = Base.Form.useWatch<InputCellSetting['empty']>('empty', { form }) === 'yes';

  const initialValues: CustomInputModalForm = {
    title: '',
    field: 'A1',
    empty: CellEmpty.NO,
    placeholder: '',
    type: CellType.NUMBER,
    fx: '',
    id: '',
    condition: [],
    enable: CellEnable.ENABLED,
    optional: CellOptional.YES,
    copy: false
  };

  console.info(initialValues, '==initialValues==');

  async function handleShow(value: InputCellSetting, pos: CellPos): Promise<InputCellSetting> {
    // 单元格转换为 A1 样式的字符串
    form.setFieldValue('field', service.posToString(pos));
    // 初始化进入传递的是{title; ''}
    const initValue = JSON.stringify(value) === JSON.stringify({ title: '' }) ? initialValues : value;
    setInitValue(Object.assign({}, initialValues, initValue));
    form.setFieldsValue(Object.assign({}, initialValues, initValue));
    const condition = form.getFieldValue('condition');
    if (condition != null) {
      setCondition(condition);
    }
    setShow(true);
    return await new Promise(resolve => confirm.current = resolve);
  }

  async function handleOk(): Promise<void> {
    const value = await form.validateFields();
    debug.form('field', value.field, value);
    confirm.current(value);
    setShow(false);
  }

  async function handleCancel(): Promise<void> {
    form.resetFields();
    form.setFieldsValue(Object.assign({}, initialValues, initValue ?? {}));
    setInitValue(initValue);
    confirm.current(initValue ?? initialValues);
    setShow(false);
  }

  function handleConditionChange(value: Condition[]): void {
    const result = value.map(item => ({
      min: item.min,
      max: item.max,
      value: item.value
    }));
    form.setFieldValue('condition', result);
    setCondition(result);
  }

  useImperativeHandle(ref, () => ({ show: handleShow }));

  return (
    <Base.Modal title={t('单元格 {field} 属性设置', { field: form.getFieldValue('field') as string })}
      width={720}
      open={show}
      onCancel={handleCancel}
      maskClosable={false}
      footer={(
        <ModalFooter
          copy={props.copy === true}
          empty={empty}
          condition={condition}
          onOk={handleOk}
          onCancel={handleCancel}
          onConditionChange={handleConditionChange}
        />
      )}>
      <Box className={modalStyles.basicContainer}>
        <Base.Form form={form} initialValues={initialValues} layout='vertical'>
          <Base.Row>
            <Base.Col span={6}>
              <Base.Form.Item name='empty' label={t('置空')}>
                <Base.Radio.Group buttonStyle='solid' className={styles.antRadioButtonCustom}>
                  <Base.Radio.Button value='no'>{t('关闭')}</Base.Radio.Button>
                  <Base.Radio.Button value='yes'>{t('置空')}</Base.Radio.Button>
                </Base.Radio.Group>
              </Base.Form.Item>
            </Base.Col>
            <Base.Col span={6}>
              <Base.Form.Item name='type' label={t('类型')}>
                <Base.Radio.Group buttonStyle='solid' disabled={empty} className={styles.antRadioButtonCustom}>
                  <Base.Radio.Button value='text'>{t('文本')}</Base.Radio.Button>
                  <Base.Radio.Button value='number'>{t('数字')}</Base.Radio.Button>
                </Base.Radio.Group>
              </Base.Form.Item>
            </Base.Col>
            <Base.Col span={6}>
              <Base.Form.Item name='enable' label={t('编辑')}>
                <Base.Radio.Group buttonStyle='solid' disabled={empty} className={styles.antRadioButtonCustom}>
                  <Base.Radio.Button value='enabled'>{t('允许')}</Base.Radio.Button>
                  <Base.Radio.Button value='disabled'>{t('禁止')}</Base.Radio.Button>
                </Base.Radio.Group>
              </Base.Form.Item>
            </Base.Col>
            <Base.Col span={6}>
              <Base.Form.Item name='optional' label={t('选填')}>
                <Base.Radio.Group buttonStyle='solid' disabled={empty} className={styles.antRadioButtonCustom}>
                  <Base.Radio.Button value='no'>{t('必须')}</Base.Radio.Button>
                  <Base.Radio.Button value='yes'>{t('选填')}</Base.Radio.Button>
                </Base.Radio.Group>
              </Base.Form.Item>
            </Base.Col>
          </Base.Row>
          <Base.Form.Item name='fx' label={t('公式')}>
            <Base.Input placeholder={t('公式')} disabled={empty} />
          </Base.Form.Item>
          <Base.Form.Item name='placeholder' label={t('描述')}>
            <Base.Input placeholder={t('描述')} disabled={empty} />
          </Base.Form.Item>
        </Base.Form>
      </Box>
    </Base.Modal>
  );
}

export const CustomInputModal = forwardRef(CustomInputModalComponent);
